import type { Attachment } from "@/lib/models";
import { Calendar, Trash, Trash2 } from "lucide-react";
import DeleteDialog from "./DeleteDialog";
import { formatDateTimeDisplayString } from "@/lib/string-utils";
import { useState } from "react";
import { Card, CardContent } from "./ui/card";

interface ImageListProps {
  attachments: Attachment[];
  onDeleteAttachment: (attachmentId: string) => void;
  attachmentColumn: boolean;
}

const ImageItem: React.FC<{ image: Attachment; onDelete: (id: string) => void }> = ({ image, onDelete }) => (
  <div key={image.id}>
    <img
      src={image.url}
      alt="Gallery image"
      className="w-full h-32 object-cover rounded-lg"
    />
    <div className="flex items-center justify-between m-1">
      <span className="text-sm text-gray-600">
        {formatDateTimeDisplayString(image.dateTaken ?? image.uploadDate)}
      </span>
      <button
        onClick={() => {
          onDelete(image.id);
        }}
        title="delete gallery image"
      >
        <Trash size={16} />
      </button>
    </div>
  </div>
);

const ImageList: React.FC<ImageListProps> = ({ attachments, onDeleteAttachment, attachmentColumn }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteAttachmentId, setDeleteAttachmentId] = useState("")

  const handleDeleteClick = (id: string) => {
    setDeleteAttachmentId(id);
    setShowDeleteDialog(true);
  };

  return (
      <>
        {!attachmentColumn ? (
          <>
            {attachments.map((att) => (
            <Card key={att.id} className="mt-1 pt-2 h-72 flex flex-col">
              <CardContent className="text-sm text-gray-600 h-full flex flex-col">
                <div className="flex flex-col h-full">
                  <img
                    src={att.url}
                    alt={att.filename}
                    className="w-full flex-1 object-cover rounded-md mb-2 min-h-0"
                    style={{ flexGrow: 1 }}
                  />
                  <div className="flex justify-between items-center mt-auto">
                    <p>
                      <Calendar className="inline-block h-4 w-4 align-text-bottom" />{" "}
                      {formatDateTimeDisplayString(att.dateTaken ?? att.uploadDate)}
                    </p>
                    <button className="px-3 py-1 text-sm hover:text-red-600 cursor-pointer" onClick={() => {
                      handleDeleteClick(att.id)
                    }}>
                      <Trash2 className="h-4 w-4 mr-2" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
            ))}
          </>
        ) : (
          <>
            <div className="flex-1 p-1">
              {attachments
                .filter((_, index) => index % 2 === 0) // Even-indexed images (0, 2, 4, ...)
                .map((image) => (
                  <ImageItem key={image.id} image={image} onDelete={handleDeleteClick} />
                ))}
            </div>
            <div className="flex-1 p-1">
              {attachments
                .filter((_, index) => index % 2 !== 0) // Odd-indexed images (1, 3, 5, ...)
                .map((image) => (
                  <ImageItem key={image.id} image={image} onDelete={handleDeleteClick} />
                ))}
            </div>
          </>
        )}
      <DeleteDialog
        deleteConfirmationText="Are you sure you want to delete the attachment?"
        isOpen={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Attachment"
        onDelete={async () => {
          if (!deleteAttachmentId) {
            alert("No attachment selected for deletion");
            return;
          }
          onDeleteAttachment(deleteAttachmentId);
        }}
      />
    </>
  );
};

export default ImageList;
